<template>
  <div class="project-notifications">
    <AppPageHeader 
      :subtitle="$t('notifications.projectSettings.description')"
    />

    <AppCard v-if="loading" class="loading-card">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-text">{{ $t('common.loading') }}</p>
      </div>
    </AppCard>

    <AppCard v-else-if="error" class="error-card">
      <div class="error-content">
        <div class="error-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h3 class="error-title">{{ $t('notifications.projectSettings.loadingError') }}</h3>
        <p class="error-message">{{ error }}</p>
        <AppButton @click="loadSettings" variant="primary" size="sm">
          {{ $t('common.retry') }}
        </AppButton>
      </div>
    </AppCard>

    <AppCard v-else class="settings-card">
      <form @submit.prevent="saveSettings" class="settings-form">
        <!-- Email Notifications Toggle -->
        <div class="form-group">
          <div class="toggle-group">
            <label class="toggle-label">
              {{ $t('notifications.projectSettings.emailNotifications') }}
            </label>
            <div class="toggle-wrapper">
              <input
                v-model="formData.emailNotificationsEnabled"
                type="checkbox"
                class="toggle-input"
                id="email-notifications-toggle"
              />
              <label for="email-notifications-toggle" class="toggle-switch">
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-status">
                {{ formData.emailNotificationsEnabled ? $t('notifications.projectSettings.enabled') : $t('notifications.projectSettings.disabled') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Balance Limit Notifications Section -->
        <div class="form-group">
          <div class="toggle-group">
            <label class="toggle-label">
              {{ $t('notifications.projectSettings.balanceLimit') }}
              <span class="toggle-description">{{ $t('notifications.projectSettings.balanceLimitDescription') }}</span>
            </label>
            <div class="toggle-wrapper">
              <input
                v-model="formData.balanceLimitNotificationsEnabled"
                type="checkbox"
                class="toggle-input"
                id="balance-limit-toggle"
                :disabled="!formData.emailNotificationsEnabled"
              />
              <label for="balance-limit-toggle" class="toggle-switch" :class="{ 'disabled': !formData.emailNotificationsEnabled }">
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-status">
                {{ formData.balanceLimitNotificationsEnabled ? $t('notifications.projectSettings.enabled') : $t('notifications.projectSettings.disabled') }}
              </span>
            </div>
          </div>

          <!-- Threshold Amount Input -->
          <div v-if="formData.balanceLimitNotificationsEnabled && formData.emailNotificationsEnabled" class="threshold-input">
            <label for="balance-threshold" class="threshold-label">
              {{ $t('notifications.projectSettings.threshold') }}
            </label>
            <div class="input-group">
              <span class="input-prefix">{{ currencySymbol }}</span>
              <input
                v-model.number="formData.balanceLimitThreshold"
                type="number"
                id="balance-threshold"
                class="form-input"
                :placeholder="$t('notifications.projectSettings.thresholdPlaceholder')"
                min="0"
                step="0.01"
                :class="{ 'error': validationErrors.balanceLimitThreshold }"
              />
            </div>
            <span v-if="validationErrors.balanceLimitThreshold" class="error-message">
              {{ validationErrors.balanceLimitThreshold }}
            </span>
          </div>
        </div>

        <!-- Email Type Preferences Section -->
        <div v-if="formData.emailNotificationsEnabled" class="form-group email-types-section">
          <h3 class="section-title">
            <i class="fas fa-envelope-open-text"></i>
            {{ $t('notifications.projectSettings.emailTypePreferences') }}
          </h3>
          <p class="section-description">
            {{ $t('notifications.projectSettings.emailTypeDescription') }}
          </p>

          <div class="email-types-grid">
            <div 
              v-for="emailType in emailTypePreferences" 
              :key="emailType.emailType"
              class="email-type-card"
            >
              <div class="email-type-header">
                <div class="email-type-info">
                  <h4 class="email-type-name">{{ getEmailTypeName(emailType.emailType) }}</h4>
                  <p class="email-type-description">{{ getEmailTypeDescription(emailType.emailType) }}</p>
                </div>
                <div class="email-type-toggle">
                  <div class="toggle-wrapper">
                    <input
                      v-model="emailType.enabled"
                      type="checkbox"
                      class="toggle-input"
                      :id="`email-type-${emailType.emailType}`"
                      @change="updateEmailTypePreference(emailType)"
                    />
                    <label :for="`email-type-${emailType.emailType}`" class="toggle-switch toggle-switch-sm">
                      <span class="toggle-slider"></span>
                    </label>
                    <span class="toggle-status toggle-status-sm">
                      {{ emailType.enabled ? $t('notifications.projectSettings.enabled') : $t('notifications.projectSettings.disabled') }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="emailType.lastSent" class="email-type-meta">
                <span class="last-sent">
                  {{ $t('notifications.projectSettings.lastSent') }}: {{ formatDate(emailType.lastSent) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="form-actions">
          <AppButton 
            type="submit" 
            variant="primary" 
            :loading="saving"
            :disabled="!isFormValid || saving"
          >
            {{ $t('notifications.projectSettings.save') }}
          </AppButton>
          
          <AppButton 
            @click="resetForm" 
            variant="outline" 
            :disabled="saving"
          >
            {{ $t('notifications.projectSettings.reset') }}
          </AppButton>
        </div>
      </form>
    </AppCard>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';

// Components
import AppCard from '../../base/AppCard.vue';
import AppButton from '../../base/AppButton.vue';
import AppPageHeader from '../../base/AppPageHeader.vue';

// Services
import projectNotificationsService from '@/services/projectNotifications.service';

interface Props {
  projectId: string | number;
}

const props = defineProps<Props>();

// Composables
const { t } = useI18n();
const { notifySuccess, notifyError } = useNotifications();

// Reactive data
const loading = ref<boolean>(false);
const saving = ref<boolean>(false);
const error = ref<string | null>(null);
const hasSettings = ref<boolean>(false);
const originalData = ref<any | null>(null);

const formData = ref<{ emailNotificationsEnabled: boolean; balanceLimitNotificationsEnabled: boolean; balanceLimitThreshold: number | null }>({
  emailNotificationsEnabled: false,
  balanceLimitNotificationsEnabled: false,
  balanceLimitThreshold: null
});

const emailTypePreferences = ref<Array<{ emailType: string; enabled: boolean; lastSent: string | null }>>([]);
const validationErrors = ref<Record<string, string>>({});

// Computed properties
const currencySymbol = computed(() => {
  // You can get this from user settings or project settings
  return '$'; // Default, could be made dynamic
});

const isFormValid = computed(() => {
  return Object.keys(validationErrors.value).length === 0;
});

// Watchers
watch(
  () => formData.value,
  (newData: any) => {
    validateForm(newData);
  },
  { deep: true }
);

// Methods
const loadSettings = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const response = await projectNotificationsService.getNotificationSettings(props.projectId);
    
    if (response) {
      formData.value = {
        emailNotificationsEnabled: response.emailNotificationsEnabled,
        balanceLimitNotificationsEnabled: response.balanceLimitNotificationsEnabled,
        balanceLimitThreshold: response.balanceLimitThreshold
      };
      originalData.value = { ...formData.value };
      hasSettings.value = true;
      
      // Load email type preferences if email notifications are enabled
      if (response.emailNotificationsEnabled) {
        await loadEmailTypePreferences();
      } else {
        initializeDefaultEmailTypes();
      }
    } else {
      hasSettings.value = false;
      initializeDefaultEmailTypes();
    }
  } catch (err) {
    console.error('Error loading notification settings:', err);
    const e: any = err;
    error.value = e?.response?.data?.message || t('notifications.projectSettings.loadingError');
    hasSettings.value = false;
    initializeDefaultEmailTypes();
  } finally {
    loading.value = false;
  }
};

const validateForm = (data: any) => {
  const validation = projectNotificationsService.validateSettings(data);
  validationErrors.value = validation.errors || {};
};

const saveSettings = async () => {
  if (!isFormValid.value) return;
  
  saving.value = true;
  
  try {
    const settingsData = {
      projectId: props.projectId,
      emailNotificationsEnabled: formData.value.emailNotificationsEnabled,
      balanceLimitNotificationsEnabled: formData.value.balanceLimitNotificationsEnabled,
      balanceLimitThreshold: formData.value.balanceLimitThreshold
    };

    if (hasSettings.value) {
      await projectNotificationsService.updateNotificationSettings(props.projectId, settingsData);
    } else {
      await projectNotificationsService.createNotificationSettings(props.projectId, settingsData);
      hasSettings.value = true;
    }
    
    originalData.value = { ...formData.value };
    notifySuccess(t('notifications.projectSettings.settingsSaved'));
  } catch (err) {
    console.error('Error saving notification settings:', err);
    const e: any = err;
    notifyError(e?.response?.data?.message || t('notifications.projectSettings.settingsError'));
  } finally {
    saving.value = false;
  }
};

const resetForm = () => {
  if (originalData.value) {
    formData.value = { ...originalData.value };
  } else {
    formData.value = {
      emailNotificationsEnabled: false,
      balanceLimitNotificationsEnabled: false,
      balanceLimitThreshold: null
    };
  }
  validationErrors.value = {};
};

// Email Type Preferences Methods
const loadEmailTypePreferences = async () => {
  try {
    const preferences = await projectNotificationsService.getEmailTypePreferences(props.projectId);
    emailTypePreferences.value = preferences;
  } catch (err) {
    console.error('Error loading email type preferences:', err);
    // Initialize with default preferences if loading fails
    initializeDefaultEmailTypes();
  }
};

const initializeDefaultEmailTypes = () => {
  const defaultTypes = [
    'PROJECT_CREATION',
    'PAYMENT_CONFIRMATION', 
    'TRANSACTION_NOTIFICATION',
    'DOCUMENT_SHARING',
    'PROJECT_COLLABORATION_INVITATION',
    'PROJECT_INVITE',
    'PROJECT_INVITE_OTP',
    'TASK_NOTIFICATION',
    'BALANCE_LIMIT_NOTIFICATION'
  ];

  emailTypePreferences.value = defaultTypes.map(type => ({
    emailType: type,
    enabled: false, // All email types disabled by default - notifications should be opt-in
    lastSent: null
  }));
};

const updateEmailTypePreference = async (emailType: { emailType: string; enabled: boolean }) => {
  try {
    await projectNotificationsService.updateEmailTypePreference(
      props.projectId, 
      emailType.emailType, 
      emailType.enabled
    );
    
    notifySuccess(t('notifications.projectSettings.emailTypeUpdated'));
  } catch (err) {
    console.error('Error updating email type preference:', err);
    // Revert the change on error
    emailType.enabled = !emailType.enabled;
    const e: any = err;
    notifyError(e?.response?.data?.message || t('notifications.projectSettings.emailTypeUpdateError'));
  }
};

const getEmailTypeName = (emailType: string) => {
  const typeNames: Record<string, string> = {
    'PROJECT_CREATION': t('notifications.emailTypes.projectCreation.name'),
    'PAYMENT_CONFIRMATION': t('notifications.emailTypes.paymentConfirmation.name'),
    'TRANSACTION_NOTIFICATION': t('notifications.emailTypes.transactionNotification.name'),
    'DOCUMENT_SHARING': t('notifications.emailTypes.documentSharing.name'),
    'PROJECT_COLLABORATION_INVITATION': t('notifications.emailTypes.collaborationInvitation.name'),
    'PROJECT_INVITE': t('notifications.emailTypes.projectInvite.name'),
    'PROJECT_INVITE_OTP': t('notifications.emailTypes.projectInviteOtp.name'),
    'TASK_NOTIFICATION': t('notifications.emailTypes.taskNotification.name'),
    'BALANCE_LIMIT_NOTIFICATION': t('notifications.emailTypes.balanceLimitNotification.name')
  };
  return typeNames[emailType] ?? emailType.replace('_', ' ');
};

const getEmailTypeDescription = (emailType: string) => {
  const typeDescriptions: Record<string, string> = {
    'PROJECT_CREATION': t('notifications.emailTypes.projectCreation.description'),
    'PAYMENT_CONFIRMATION': t('notifications.emailTypes.paymentConfirmation.description'),
    'TRANSACTION_NOTIFICATION': t('notifications.emailTypes.transactionNotification.description'),
    'DOCUMENT_SHARING': t('notifications.emailTypes.documentSharing.description'),
    'PROJECT_COLLABORATION_INVITATION': t('notifications.emailTypes.collaborationInvitation.description'),
    'PROJECT_INVITE': t('notifications.emailTypes.projectInvite.description'),
    'PROJECT_INVITE_OTP': t('notifications.emailTypes.projectInviteOtp.description'),
    'TASK_NOTIFICATION': t('notifications.emailTypes.taskNotification.description'),
    'BALANCE_LIMIT_NOTIFICATION': t('notifications.emailTypes.balanceLimitNotification.description')
  };
  return typeDescriptions[emailType] ?? '';
};

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString();
};

// Lifecycle
onMounted(() => {
  loadSettings();
});
</script>

<style scoped>
.project-notifications {
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem;
}

.loading-card,
.error-card,
.settings-card,
.no-settings-card {
  margin-bottom: 1rem;
}

.loading-content,
.error-content,
.no-settings-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon,
.no-settings-icon {
  font-size: 3rem;
  color: var(--neutral-400);
  margin-bottom: 1rem;
}

.error-icon {
  color: var(--danger);
}

.settings-form {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 2rem;
}

.toggle-group {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.toggle-label {
  flex: 1;
  font-weight: 600;
  color: var(--text-primary);
  margin-right: 1rem;
}

.toggle-description {
  display: block;
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toggle-input {
  display: none;
}

.toggle-switch {
  position: relative;
  width: 48px;
  height: 24px;
  background-color: var(--neutral-300);
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.toggle-switch.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-input:checked + .toggle-switch {
  background-color: var(--primary);
}

.toggle-input:checked + .toggle-switch .toggle-slider {
  transform: translateX(24px);
}

.toggle-status {
  font-size: 0.875rem;
  color: var(--text-secondary);
  min-width: 80px;
}

.threshold-input {
  margin-left: 2rem;
  margin-top: 1rem;
}

.threshold-label {
  display: block;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.input-group {
  display: flex;
  align-items: center;
  position: relative;
}

.input-prefix {
  position: absolute;
  left: 12px;
  color: var(--text-secondary);
  font-weight: 500;
  z-index: 1;
}

.form-input {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2rem;
  border: 2px solid var(--neutral-200);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary);
}

.form-input.error {
  border-color: var(--danger);
}

.error-message {
  display: block;
  color: var(--danger);
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--neutral-200);
}

/* Email Types Section */
.email-types-section {
  border-top: 1px solid var(--neutral-100);
  padding-top: 2rem;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
}

.section-title i {
  color: var(--primary);
}

.section-description {
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
}

.email-types-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
}

.email-type-card {
  background: var(--neutral-50);
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;
}

.email-type-card:hover {
  border-color: var(--primary-200);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.email-type-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.email-type-info {
  flex: 1;
}

.email-type-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
}

.email-type-description {
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.4;
  margin: 0;
}

.email-type-toggle {
  flex-shrink: 0;
}

.email-type-toggle .toggle-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.toggle-switch-sm {
  width: 36px;
  height: 20px;
  border-radius: 10px;
}

.toggle-switch-sm .toggle-slider {
  width: 16px;
  height: 16px;
  top: 2px;
  left: 2px;
}

.toggle-input:checked + .toggle-switch-sm .toggle-slider {
  transform: translateX(16px);
}

.toggle-status-sm {
  font-size: 0.75rem;
  color: var(--text-secondary);
  text-align: center;
  min-width: auto;
}

.email-type-meta {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--neutral-200);
}

.last-sent {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

/* Responsive Design */
@media (max-width: 768px) {
  .project-notifications {
    padding: 0.5rem;
  }
  
  .toggle-group {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .toggle-wrapper {
    margin-top: 0.5rem;
  }
  
  .threshold-input {
    margin-left: 0;
  }
  
  .form-actions {
    flex-direction: column;
  }
}
</style>
